import React from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { ArrowBack, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppealList } from '../../hooks/useAppeal';
import type { AppealStatus, SanctionType, ReviewResult } from '../../types/report';
import { formatDateKST, parseKSTDate } from '../../utils/dateUtils';

const STATUS_CONFIG: Record<AppealStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
  SUBMITTED: { label: '접수됨', color: 'default' },
  UNDER_REVIEW: { label: '심사 중', color: 'primary' },
  ACCEPTED: { label: '이의 인정', color: 'success' },
  REJECTED: { label: '이의 기각', color: 'error' },
  CLOSED: { label: '종결', color: 'default' },
};

const SANCTION_LABELS: Record<SanctionType, string> = {
  WARNING: '경고',
  SUSPENSION: '이용 정지',
  BAN: '영구 차단',
};

const REVIEW_LABELS: Record<ReviewResult, string> = {
  ACCEPTED: '인정',
  REJECTED: '기각',
};

const AppealListPage: React.FC = () => {
  const navigate = useNavigate();
  const { appeals, loading, error } = useAppealList();


  const isDeadlineSoon = (deadline: string) => {
    const diff = parseKSTDate(deadline).getTime() - Date.now();
    return diff > 0 && diff < 3 * 24 * 60 * 60 * 1000; // 3일 이내
  };

  const isDeadlinePassed = (deadline: string) => parseKSTDate(deadline).getTime() < Date.now();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>이의제기 내역</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && appeals.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '1rem', mb: 3 }}>
              접수된 이의제기가 없습니다.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/appeal')}
              sx={{ py: 1.5, px: 4, fontSize: '1rem', fontWeight: 700 }}
            >
              이의제기하기
            </Button>
          </Box>
        )}

        {appeals.map((appeal) => {
          const status = STATUS_CONFIG[appeal.status];
          const deadlineSoon = isDeadlineSoon(appeal.deadline);
          const deadlinePassed = isDeadlinePassed(appeal.deadline);

          return (
            <Card
              key={appeal.id}
              elevation={0}
              sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
              onClick={() => navigate(`/appeal/${appeal.id}`)}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={SANCTION_LABELS[appeal.sanctionType]}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.8rem', height: 24 }}
                    />
                    <Chip
                      label={status.label}
                      size="small"
                      color={status.color}
                      sx={{ fontSize: '0.8rem', height: 24 }}
                    />
                    {appeal.reviewResult && (
                      <Chip
                        label={`심사 결과: ${REVIEW_LABELS[appeal.reviewResult]}`}
                        size="small"
                        color={appeal.reviewResult === 'ACCEPTED' ? 'success' : 'error'}
                        variant="outlined"
                        sx={{ fontSize: '0.8rem', height: 24 }}
                      />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                      접수일: {formatDateKST(appeal.createdAt)}
                    </Typography>
                    {appeal.reviewedAt && (
                      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                        심사 완료: {formatDateKST(appeal.reviewedAt)}
                      </Typography>
                    )}
                    {!deadlinePassed && (
                      <Typography
                        sx={{
                          fontSize: '0.82rem',
                          color: deadlineSoon ? 'error.main' : 'text.secondary',
                          fontWeight: deadlineSoon ? 700 : 400,
                        }}
                      >
                        기한: {formatDateKST(appeal.deadline)}{deadlineSoon ? ' (기한 임박)' : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Container>
    </Box>
  );
};

export default AppealListPage;
